import {ClientReportColumn, ClientReportInfo, ClientReportReportStatusMap, ClientReportReportType, ClientReportReportTypeMap} from "./domain";
import React from "react";
import {get, uniqBy} from 'lodash';
import {formatDate, isValidDate} from "../../common/util/date";
import {Link} from "evergreen-ui";
import { ModalTooltip } from "common/component/ModalTooltip";


type ClientReportRowItemProps = {
    report: ClientReportInfo;
    hideModalFunc: () => void;
    setSelectedFunc: (selected: ClientReportInfo | null) => void;
    columns: ClientReportColumn[],
    selected: boolean,
    isApplyGroupBySfOperationCase?: boolean;
}

export function ClientReportRowItem({
                                        report,
                                        selected,
                                        hideModalFunc,
                                        setSelectedFunc,
                                        columns,
                                        isApplyGroupBySfOperationCase,
                                    }: ClientReportRowItemProps) {
    const escFunction = React.useCallback((event) => {
        if (event.keyCode === 27) {
            hideModalFunc();
            setSelectedFunc(null);
        }
    }, [hideModalFunc, setSelectedFunc]);

    React.useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    const displayColumn = (column: ClientReportColumn) => {
        if (column.attr === 'submittedBy') {
            return get(report, 'submittedBy.firstName') + " " + get(report, 'submittedBy.lastName');
        }

        if (column.attr === 'clientReportFiles') {
            const reportFiles = get(report, 'clientReportFiles') || [];
            const sfOperationCaseNumber = report.sfOperationCaseNumber ?? '';
            if (!isApplyGroupBySfOperationCase ||  sfOperationCaseNumber === '') {
                return reportFiles.map((f) => (
                    <Link key={f.guid} target="_blank" rel="noopener" href={f.downloadUrl}>
                        <span className="text-blue-portal-link">
                            {f.fileName} <br/>
                        </span>
                    </Link>
                ));
            }
            const linkedReports = get(report, 'clientReportFilesBySfOperationCase') || [];
            const filetedDuplicateReports = ClientReportReportTypeMap.get(report.reportType) === ClientReportReportType.CHANGE_HOURS_ADDENDUM ? uniqBy(linkedReports, 'fileName') : linkedReports;

            if (filetedDuplicateReports.length > 0) {
                return (
                    <>
                        {filetedDuplicateReports.map((f) => (
                            <Link key={f.guid} target="_blank" rel="noopener" href={f.downloadUrl}>
                                <span className="text-blue-portal-link">
                                    {f.fileName} <br/>
                                </span>
                            </Link>
                        ))}
                    </>
                );
            }

            return "None";
        }

        if (column.attr === 'reportType') {
            return (
                <span className="flex items-center gap-2">
                    {ClientReportReportTypeMap.get(get(report, 'reportType'))}
                    {report.shared && (
                        <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
                            GENERATED
                        </span>
                    )}
                </span>
            );
        }

        if (column.attr === 'reportStatus') {
            return <div className="flex items-center">
                {ClientReportReportStatusMap.get(report.reportStatus)}
                {report.sfCloseReason && <ModalTooltip content={report.sfCloseReason} position="left"/>}
            </div>
        }

        if (column.attr === 'authorizedDate' 
                && report.sfCloseReason != null 
                && report.sfCloseDate != null) {
            if (isValidDate(new Date(report.sfCloseDate))) {
                return formatDate(new Date(report.sfCloseDate));
            }
        }

        const val = get(report, column.attr);
        if (val === null) {
            return "None";
        }
        if (column.format === "string") {
            return val;
        }
        if (column.format === "string[]") {
            return val.join(", ");
        }

        if (column.format === "date") {
            if (isValidDate(new Date(val))) {
                return formatDate(new Date(val));
            }
        }

        return "";
    }

    return (
        <tr className={`border-b border-gray-200 hover:${selected ? 'bg-botticelli-100' : 'bg-gray-200'} ${selected ? 'bg-botticelli-100 text-white' : ''}`}
            onClick={() => setSelectedFunc(report)}>
            {
                columns.map((column) => (
                    <td
                        key={column.name}
                        className={'py-5' + (column.attr === 'clientReportFiles' ? ' pl-4' : '')}
                    >
                        {
                            displayColumn(column)
                        }
                    </td>
                ))
            }
        </tr>
    )

}
